/*************************************************/
/* Slider                                        */
/*************************************************/
import Swiper from 'swiper';
import lottie from "lottie-web";
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import customEventsNames from './../customEventsNames';

Swiper.use([Navigation, Pagination, Autoplay]);

export function slider() {
    const contentSliderSelector = ".content-slider__slides.swiper";
    const contentBoxesSelector = ".content-boxes__content-boxes.swiper";
    const testimonialSliderSelector = ".testimonial-slider__slider.swiper";
    const cardsSliderSelector = ".cards__list-container.swiper";
    const contentSliderWebsiteSelector = ".content-slider-website__slides.swiper";
    const contentBoxesWebsiteSelector = ".content-boxes__boxes-wrap.swiper";

    const handleSlideChangeTransitionEnd = function (swiper) {
        // Fix the tabindex for accessibility
        swiper.el.querySelectorAll(".swiper-slide > a").forEach((slide) => {
            slide.setAttribute("tabindex", "-1");
        });

        const activeSlide = swiper.el.querySelector(".swiper-slide-active > a");
        if (activeSlide != null) {
            activeSlide.setAttribute("tabindex", "0");
        }
    };

    const handleSlideChange = function () {
        // Ensure that the lazyloaded image on the duplicate slide has the correct class
        const nextDuplicateSlide = document.querySelector(".swiper-slide-duplicate.swiper-slide-next");
        if (nextDuplicateSlide != null) {
            const unloadedSlideImage = nextDuplicateSlide.querySelector("img.lazyloading");
            if (unloadedSlideImage != null) {
                unloadedSlideImage.classList.replace("lazyloading", "lazyloaded");
            }
        }
    }

    const init = function () {
        // Content Slider
        const contentSlider = [];
        const contentSliderElements = document.querySelectorAll(contentSliderSelector);
        if (contentSliderElements.length > 0) {
            contentSliderElements.forEach(slider => {
                contentSlider.push(new Swiper(slider, {
                    slidesPerView: "auto",
                    centeredSlides: true,
                    loop: true,
                    initialSlide: 0,
                    speed: 600,
                    pagination: {
                        el: `#${slider.dataset.componentId} .swiper-pagination`,
                        clickable: true,
                    },
                    navigation: {
                        nextEl: `#${slider.dataset.componentId} .content-slider__button-next`,
                        prevEl: `#${slider.dataset.componentId} .content-slider__button-prev`,
                    },
                    on: {
                        slideChange: () => {
                            handleSlideChange();
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            const activeSlide = swiper.slides[swiper.activeIndex];
                            const lottieAnimation = activeSlide.closest('.lottie');

                            if (lottieAnimation && lottieAnimation.lottieInstance) {
                                lottieAnimation.lottieInstance.goToAndStop(0, true); // Reset to the beginning
                                lottieAnimation.lottieInstance.play();
                            }

                            handleSlideChangeTransitionEnd(swiper);
                        }
                    }
                }));
            });
        }

        // Content Boxes
        const contentBoxes = [];
        const contentBoxesElements = document.querySelectorAll(contentBoxesSelector);
        if (contentBoxesElements.length > 0) {
            contentBoxesElements.forEach(slider => {
                contentBoxes.push(new Swiper(slider, {
                    slidesPerView: 1.2,
                    spaceBetween: 20,
                    breakpoints: {
                        768: {
                            slidesPerView: 2,
                            spaceBetween: 40,
                        }
                    },
                    navigation: {
                        nextEl: `#${slider.dataset.componentId} .content-boxes__button-next`,
                        prevEl: `#${slider.dataset.componentId} .content-boxes__button-prev`,
                    },
                    on: {
                        slideChange: () => {
                            handleSlideChange();
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            handleSlideChangeTransitionEnd(swiper);
                        }
                    }
                }));
            });
        }

        // Testimonial Slider
        const testimonialSlider = [];
        const testimonialSliderElements = document.querySelectorAll(testimonialSliderSelector);
        if (testimonialSliderElements.length > 0) {
            testimonialSliderElements.forEach(slider => {
                testimonialSlider.push(new Swiper(slider, {
                    slidesPerView: 1,
                    navigation: {
                        nextEl: `#${slider.dataset.componentId} .testimonial-slider__button-next`,
                        prevEl: `#${slider.dataset.componentId} .testimonial-slider__button-prev`,
                    },
                    on: {
                        slideChange: () => {
                            handleSlideChange();
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            handleSlideChangeTransitionEnd(swiper);
                        }
                    }
                }));
            });
        }

        // Cards Slider
        const cardsSlider = [];
        const cardsSliderElements = document.querySelectorAll(cardsSliderSelector);
        if (cardsSliderElements.length > 0) {
            cardsSliderElements.forEach(slider => {
                cardsSlider.push(new Swiper(slider, {
                    slidesPerView: 1.2,
                    spaceBetween: 15,
                    navigation: {
                        nextEl: `#${slider.dataset.componentId} .cards__button-next`,
                        prevEl: `#${slider.dataset.componentId} .cards__button-prev`,
                    },
                    on: {
                        slideChange: () => {
                            handleSlideChange();
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            handleSlideChangeTransitionEnd(swiper);
                        }
                    }
                }));
            });
        }

        // Content Slider Website
        const contentSliderWebsite = [];
        const contentSliderWebsiteElements = document.querySelectorAll(contentSliderWebsiteSelector);
        if (contentSliderWebsiteElements) {
            contentSliderWebsiteElements.forEach((slider, sliderIndex) => {

                const swiperInstance = new Swiper(slider, {
                    slidesPerView: 1,
                    loop: true,
                    speed: 700,
                    centeredSlides: true,
                    initialSlide: 0,
                    autoplay: {
                        enabled: false
                    },
                    breakpoints: {
                        992: {
                            autoplay: {
                                enabled: true,
                                delay: 4500,
                                disableOnInteraction: true,
                            },
                        }
                    },
                    pagination: {
                        el: ".swiper-pagination",
                        clickable: true,
                        renderBullet: function (index, className) {
                            // Helper function to find the correct slide by data-swiper-slide-index
                            const getSlideTag = (index) => {
                                const slide = Array.from(slider.querySelectorAll('.swiper-slide')).find(
                                    (el) => el.getAttribute('data-swiper-slide-index') == index
                                );
                                return slide ? slide.getAttribute("data-pagination") : `Slide ${index + 1}`;
                            };

                            // Get the tag from the correct slide
                            const tag = getSlideTag(index);

                            // Return formatted bullet with tag
                            return `<span class="${className}"><i></i>${tag}<b></b></span>`;
                        }
                    },

                    on: {
                        slideChange: () => {
                            handleSlideChange();
                            const activeBullet = document.querySelector('.swiper-pagination-bullet-active');
                            const paginationContainer = document.querySelector('.swiper-pagination');

                            if (activeBullet && paginationContainer) {
                                const bulletRect = activeBullet.getBoundingClientRect();
                                const containerRect = paginationContainer.getBoundingClientRect();

                                // Center the active bullet in the container if possible
                                const offset = bulletRect.left - containerRect.left - (containerRect.width / 2) + (bulletRect.width / 2);

                                paginationContainer.scrollBy({
                                    left: offset,
                                    behavior: 'smooth'
                                });
                            }
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            handleSlideChangeTransitionEnd(swiper);
                        },
                    }
                });

                //Handling event from playing a video in a slide
                slider.closest('.content-slider-website').addEventListener(customEventsNames.videoStartedPlayingInSlider, e => {
                    swiperInstance.autoplay.pause();
                });

                //Handling event from stopping a video in a slide
                slider.closest('.content-slider-website').addEventListener(customEventsNames.videoStoppedPlayingInSlider, e => {
                    swiperInstance.autoplay.resume();
                });

                contentSliderWebsite.push(swiperInstance);

            })
        }

        // Content Boxes Website
        const contentBoxesWebsite = [];
        const contentBoxesWebsiteElements = document.querySelectorAll(contentBoxesWebsiteSelector);
        if (contentBoxesWebsiteElements.length > 0) {
            contentBoxesWebsiteElements.forEach(slider => {
                contentBoxesWebsite.push(new Swiper(slider, {
                    slidesPerView: 1.2,
                    spaceBetween: 15,
                    navigation: {
                        nextEl: `#${slider.dataset.componentId} .content-boxes__button-next`,
                        prevEl: `#${slider.dataset.componentId} .content-boxes__button-prev`,
                    },
                    on: {
                        slideChange: () => {
                            handleSlideChange();
                        },
                        slideChangeTransitionEnd: (swiper) => {
                            handleSlideChangeTransitionEnd(swiper);
                        }
                    }
                }));
            });
        }

    }

    init();
}
